// @flow
import { combineReducers } from 'redux';

import { reducer as acl } from '@gandi/acl';

import { reducer as authenticationReducer } from '@gandi/authentication';

import {
  currenciesReducer as gandiReduxBillingCurrenciesReducer,
  infosReducer as gandiReduxBillingInfosReducer,
  meansReducer as gandiReduxBillingMeanReducer,
  moneyReducer as gandiReduxBillingMoneyReducer,
  myCardsReducer as gandiReduxBillingMyCardsReducer,
  ordersReducer as gandiReduxBillingOrdersReducer,
  prepaidReducer as gandiReduxBillingPrepaidReducer,
  pricingReducer as gandiReduxBillingPricingReducer,
} from '@gandi/billing';
import { reducer as cartReducer } from '@gandi/cart';
import { reducer as contractsReducer } from '@gandi/contracts';
import { reducer as domainContactsReducer } from '@gandi/domain-configuration';
import { reducer as notificationsReducer } from '@gandi/notifications';

import { organizationsReducer } from '@gandi/redux-organization';
import { reducer as gandiReduxPlaces } from '@gandi/redux-places';
import { reducer as gandiReduxResourceContactBookReducer } from '@gandi/redux-resource-contactbook';

import gandiReduxResourceCoupon from '@gandi/redux-resource-coupon';
import gandiReduxResourcePrice from '@gandi/redux-resource-price';
import { reducer as gandiReduxResourceSimplehosting } from '@gandi/redux-resource-simplehosting';
import { reducer as sharingReducer } from '@gandi/redux-sharing';
import { reducer as gandiReduxResourceReducer } from '@gandi/resource/redux';
import { reducer as template2Reducer } from '@gandi/template2';

import { csrfReducer, reducer as gandiFormReducer } from 'gandi.react-form';

import customersCreateReducer from './Cart/Customer/modules/customers';
import { reducer as domainContactsWizardReducer } from './Cart/DomainContacts/Wizard/modules/reducer';
import setupReducer from './Cart/Setup/modules/reducers';
import corporateReducer from './Cart/modules/corporate';

import phasesModalReducer from './DomainCreate/components/PhasesDialog/module';
import affiliateReducer from './DomainCreate/modules/affiliate';
import domainBundleReducer from './DomainCreate/modules/reducers/bundle';
import domainDiscountReducer from './DomainCreate/modules/reducers/discount';
import domainFiltersReducer from './DomainCreate/modules/reducers/filters';
import domainIDNReducer from './DomainCreate/modules/reducers/idn';
import domainPaginationReducer from './DomainCreate/modules/reducers/pagination';
import domainResultsReducer from './DomainCreate/modules/reducers/results';
import domainSearchReducer from './DomainCreate/modules/reducers/search';
import domainSourceReducer from './DomainCreate/modules/reducers/source';
import domainTLDsReducer from './DomainCreate/modules/reducers/tlds';
import renewableProductsReducer from './DomainRenew/components/modules/reducers';
import domainRenewReducer from './DomainRenew/modules/reducers';
import domainTransferReducer from './DomainTransfer/modules/reducer';
import domainTransferBulkReducer from './DomainTransferBulkResult/modules/cart';

import { reducer as simplehostingDomainReducer } from './SimpleHosting/modules/domains';
import simplehostingTypesReducer from './SimpleHosting/modules/types';

import { reducer as shsReducer } from './Studio/modules/instances';

import tmchReducer from './Tmch/modules/reducer';

import { reducer as acmeCatalogueReducer } from './certificate/modules/acme';
import { certificateReducer } from './certificate/modules/certificate';

import appReducer from './modules/app/reducer';
import { reducer as cloudAccountsReducer } from './modules/cloud/resources/accounts';
import { reducer as cloudTemplatesReducer } from './modules/cloud/resources/templates';
import { reducer as mailboxOffersReducer } from './modules/mailbox/offers';
import { reducer as mailboxStandaloneProductsReducer } from './modules/mailbox-standalone/resources/products';
import { reducer as simplehostingPricesReducer } from './modules/simplehosting/prices';
import { reducer as tldPricesReducer } from './modules/tldpublic/tlds';

const reducer = {
  // app / layout
  app: appReducer,
  notifications: notificationsReducer,
  csrf: csrfReducer,

  // authz & orga
  authentication: authenticationReducer,
  sharing: sharingReducer,

  // libs
  acl,
  acme: (combineReducers({ quote: acmeCatalogueReducer }): any),
  cart: cartReducer,
  contracts: contractsReducer,
  gandiReduxPlaces,

  // domain
  domainSuggest: (combineReducers({
    search: domainSearchReducer,
    pagination: domainPaginationReducer,
    results: domainResultsReducer,
    filters: domainFiltersReducer,
    tlds: domainTLDsReducer,
    source: domainSourceReducer,
    idn: domainIDNReducer,
    bundles: domainBundleReducer,
    discount: domainDiscountReducer,
  }): any),
  phasesModal: phasesModalReducer,
  domainRenew: domainRenewReducer,
  domainTransfer: domainTransferReducer,
  domainTransferBulk: domainTransferBulkReducer,
  // mailboxes
  mailboxOffers: mailboxOffersReducer,
  // certificates
  certificate: certificateReducer,

  // hosting
  simplehosting: (combineReducers({
    domains: simplehostingDomainReducer,
    prices: simplehostingPricesReducer,
    types: simplehostingTypesReducer,
  }): any),
  cloud: (combineReducers({
    accounts: cloudAccountsReducer,
    templates: cloudTemplatesReducer,
  }): any),

  // shared
  renewableProducts: renewableProductsReducer,

  // products configuration
  configure: (combineReducers({
    corporate: corporateReducer,
    customers: customersCreateReducer,
    domainContactsWizard: domainContactsWizardReducer,
    // templates: templatesReducer,
    setup: setupReducer,
  }): any),
  domainContacts: domainContactsReducer,
  // TMCH
  tmch: tmchReducer,
  // TLD public
  tldpublic: tldPricesReducer,
  // tools
  gandiForm: gandiFormReducer,
  gandiReduxResource: gandiReduxResourceReducer,
  gandiReduxResourceCoupon,
  gandiReduxResourcePrice,
  gandiReduxBilling: (combineReducers({
    currencies: gandiReduxBillingCurrenciesReducer,
    infos: gandiReduxBillingInfosReducer,
    means: gandiReduxBillingMeanReducer,
    money: gandiReduxBillingMoneyReducer,
    myCards: gandiReduxBillingMyCardsReducer,
    orders: gandiReduxBillingOrdersReducer,
    prepaid: gandiReduxBillingPrepaidReducer,
    pricing: gandiReduxBillingPricingReducer,
  }): any),
  gandiReduxResourceSimplehosting,
  template2: template2Reducer,
  gandiReduxOrganization: (combineReducers({
    organizations: organizationsReducer,
  }): any),
  gandiReduxResourceContactBook: gandiReduxResourceContactBookReducer,
  affiliate: affiliateReducer,
  'mailbox-standalone': (combineReducers({
    products: mailboxStandaloneProductsReducer,
  }): any),

  // simplehosting
  shs: (combineReducers({
    instances: shsReducer,
  }): any),
};

export default reducer;
